import { useIntl } from 'react-intl';

import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import {
	filterMeasurementsForContext,
	findAllMeasurementsForFerryTrip,
	getMeasurementLabelFromId,
	getMeasurementValueFromId,
} from 'Util/_HumanWritten/MeasurementUtils';
import { useMeasurementFilterContext } from './Hooks/useMeasurementFilterContext';
import { SelectInput } from 'Views/Components/New/SelectInput';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';
import { isVehicleBookingWizardData, VehicleBookingWizardData } from '../../BookingWizardData/VehicleBookingWizardData';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { WizardErrors } from '../../BookingWizardTypes';
import { useVehicleTabContext } from './VehicleTabContext';

export function TrailerLengthInput() {
	const { trailerMeasurementPrices, towOnTypes } = useVehicleTabContext();
	const intl = useIntl();
	const { wizardData, oldWizardData, setWizardData, innerWizardErrors, setInnerWizardErrors } =
		useBookingWizardDataStore();
	// Called before the early return below: hooks cannot be conditional.
	const filterContext = useMeasurementFilterContext();

	if (!isVehicleBookingWizardData(wizardData.innerWizardData)) {
		return <></>;
	}

	const innerData = wizardData.innerWizardData;
	const typedErrors = innerWizardErrors as WizardErrors<VehicleBookingWizardData>;

	const isDisabledOption = (optionId: string, minimumLengthId?: string) => {
		const optionValue = getMeasurementValueFromId(optionId);

		// Restricted (alteration) mode: can't shorten below the original trailer length.
		if (wizardData.restrictedMode && isNotNullOrUndefined(oldWizardData)) {
			const oldLengthId = (oldWizardData.innerWizardData as VehicleBookingWizardData).trailerLengthId;
			if (oldLengthId && optionValue < getMeasurementValueFromId(oldLengthId)) {
				return true;
			}
		}

		// Disable lengths below the tow-on type's minimum length (mirrors vehicles).
		if (minimumLengthId && optionValue < getMeasurementValueFromId(minimumLengthId)) {
			return true;
		}

		return false;
	};

	const getTrailerLengthOptionDisplay = (optionId: string, isDisabled: boolean) => {
		let message = '- trailer too large';
		if (wizardData.restrictedMode && isNotNullOrUndefined(oldWizardData)) {
			const oldLengthId = (oldWizardData.innerWizardData as VehicleBookingWizardData).trailerLengthId;
			if (getMeasurementValueFromId(optionId) < getMeasurementValueFromId(oldLengthId)) {
				message = '- restricted';
			}
		}

		const measurementLabel = getMeasurementLabelFromId(optionId);

		if (wizardData.isBulkBookingFlow) {
			return `${measurementLabel} ${isDisabled ? message : ''}`.trim();
		}

		// Show the full per-length cost: the length band price plus the trailer's weight band price,
		// so the dropdown reflects what the trailer will actually cost at each length. Resolve the
		// weight band the same way the server prices it: the selected band, else the tow-on type's
		// minimum, else the smallest weight band (bands come back sorted ascending).
		//
		// In ALTERATION mode the prices are length *differences* (vs the existing booking) and the
		// weight isn't changing across length options, so it must NOT be added — only add the weight
		// for a new booking where the prices are absolute.
		const lengthPrice = trailerMeasurementPrices[optionId] ?? 0;
		const weightBandId =
			innerData.trailerWeightId ||
			towOnTypes.find(t => t.id === innerData.trailerTypeId)?.minimumWeightId ||
			findAllMeasurementsForFerryTrip('WEIGHT', 'trailer')?.[0]?.id ||
			'';
		const weightPrice =
			wizardData.wizardMode !== 'ALTERATION' && weightBandId ? (trailerMeasurementPrices[weightBandId] ?? 0) : 0;

		const priceLabel = formatPriceDisplay(
			lengthPrice + weightPrice,
			wizardData.wizardMode === 'ALTERATION',
			true,
			true,
		);

		return `${measurementLabel} (${priceLabel}) ${isDisabled ? message : ''}`.trim();
	};

	const minimumLengthId = towOnTypes.find(t => t.id === innerData.trailerTypeId)?.minimumLengthId;

	// Filtered against the trailer's own characteristics (the tow-on type's plus anything ticked for
	// the trailer), with the current selection always retained.
	const visibleMeasurements = filterMeasurementsForContext(
		findAllMeasurementsForFerryTrip('LENGTH', 'trailer'),
		filterContext.trailer,
		[innerData.trailerLengthId],
	);

	const options = visibleMeasurements.map(x => {
		const isDisabled = isDisabledOption(x.id, minimumLengthId);
		return {
			value: x.id,
			label: getTrailerLengthOptionDisplay(x.id, isDisabled),
			isDisabled,
		};
	});

	const selectedOption = options.find(o => o.value === innerData.trailerLengthId) ?? null;

	const label = intl.formatMessage({
		id: 'vehicle-tab__trailer-length__label',
		defaultMessage: 'Trailer length',
	});
	const isReturnTrip = wizardData.tripType === 'return';
	const description = isReturnTrip
		? intl.formatMessage({
				id: 'vehicle-tab__trailer-length__return-price-hint',
				defaultMessage: 'Price shown includes the return trip',
			})
		: undefined;

	return (
		<SelectInput
			className="trailer-length"
			label={label}
			description={description}
			placeholder={intl.formatMessage({
				id: 'vehicle-tab__trailer-length__placeholder',
				defaultMessage: 'Select trailer length',
			})}
			options={options}
			value={selectedOption}
			required
			isSearchable
			scrollToFirstEnabledOption
			error={typedErrors?.trailerLengthId}
			onChange={(option: any) => {
				const newData = new BookingWizardData({ ...wizardData });
				if (isVehicleBookingWizardData(newData.innerWizardData)) {
					newData.innerWizardData.trailerLengthId = option?.value ?? '';
				}
				setWizardData(newData);

				const newErrors = { ...typedErrors };
				delete newErrors.trailerLengthId;
				setInnerWizardErrors(newErrors);
			}}
		/>
	);
}
